import MapScale from '@/Enums/Map/MapScale.enum';
import BeeswaxReportProperties from '@/Enums/Report/BeeswaxReportProperties.enum';
import Dv360ReportType from '@/Enums/ReportingDashboard/Dv360ReportType.enum';
import type { IGeoJson } from '@/Interfaces/Geo/GeoJson.interface';
import type { ICampaignReport } from '@/Interfaces/ReportingDashboard/Google/Report.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import type { IState } from '@/Interfaces/Slices/ReportingDashboard/Google/State.interface';
import { GEO_BOUNDARIES } from '@/constants/endpoints';
import { EMPTY_FEATURE_COLLECTION, isPostableBoundariesBody } from '@/helpers/boundaries';
import { getBoundariesRequestBody } from '@/helpers/geo';
import {
  reportRequestHandler,
  sdkReportRequestHandler,
  youtubePublishersReportRequestHandler,
  youtubeReportRequestHandler,
} from '@/helpers/reportingDashboard/google';
import type { RootState } from '@/store';
import type { RequestMap } from '@/types/RequestMap.types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

enum Action {
  GetGoogleReportTotals = 'reportingDashboardGoogle/getGoogleReportTotals',
  GetGoogleAppUrlReport = 'reportingDashboardGoogle/getGoogleAppUrlReport',
  GetGoogleDateData = 'reportingDashboardGoogle/getGoogleDateData',
  GetGoogleChannelData = 'reportingDashboardGoogle/getGoogleChannelData',
  GetGoogleDeviceData = 'reportingDashboardGoogle/getGoogleDeviceData',
  GetGoogleCampaignData = 'reportingDashboardGoogle/getGoogleCampaignData',
  GetGoogleAdvertiserData = 'reportingDashboardGoogle/getGoogleAdvertiserData',
  GetGooglePlacementData = 'reportingDashboardGoogle/getGooglePlacementData',
  GetGoogleCreativeData = 'reportingDashboardGoogle/getGoogleCreativeData',
  GetGooglePublisherData = 'reportingDashboardGoogle/getGooglePublisherData',
  GetGoogleDaysOfWeekData = 'reportingDashboardGoogle/getGoogleDaysOfWeekData',
  GetGoogleDaypartData = 'reportingDashboardGoogle/getGoogleDaypartData',
  GetGoogleStateData = 'reportingDashboardGoogle/getGoogleStateData',
  GetGoogleDmaData = 'reportingDashboardGoogle/getGoogleDmaData',
  GetDmaBoundaries = 'reportingDashboardGoogle/getDmaBoundaries',
  GetStateBoundaries = 'reportingDashboardGoogle/getStateBoundaries',
}

export const getGoogleReportTotals = createAsyncThunk(
  Action.GetGoogleReportTotals,
  sdkReportRequestHandler(Dv360ReportType.TOTAL),
);

export const getGoogleAppUrlReport = createAsyncThunk(
  Action.GetGoogleAppUrlReport,
  sdkReportRequestHandler(Dv360ReportType.APP_URL),
);

export const getGoogleDateData = createAsyncThunk(
  Action.GetGoogleDateData,
  sdkReportRequestHandler(Dv360ReportType.DATE),
);

export const getGoogleChannelData = createAsyncThunk(
  Action.GetGoogleChannelData,
  reportRequestHandler(Dv360ReportType.CHANNEL),
);

export const getGoogleDeviceData = createAsyncThunk(
  Action.GetGoogleDeviceData,
  sdkReportRequestHandler(Dv360ReportType.DEVICE),
);

export const getGoogleCampaignData = createAsyncThunk<ICampaignReport, IAbortSignalThunkArg>(
  Action.GetGoogleCampaignData,
  sdkReportRequestHandler(Dv360ReportType.CAMPAIGN),
);

export const getGoogleAdvertiserData = createAsyncThunk(
  Action.GetGoogleAdvertiserData,
  sdkReportRequestHandler(Dv360ReportType.ADVERTISER),
);

export const getGooglePlacementData = createAsyncThunk(
  Action.GetGooglePlacementData,
  sdkReportRequestHandler(Dv360ReportType.PLACEMENT),
);

export const getGoogleCreativeData = createAsyncThunk(
  Action.GetGoogleCreativeData,
  youtubeReportRequestHandler(Dv360ReportType.CREATIVE),
);

export const getGooglePublisherData = createAsyncThunk(
  Action.GetGooglePublisherData,
  youtubePublishersReportRequestHandler,
);

export const getGoogleDaysOfWeekData = createAsyncThunk(
  Action.GetGoogleDaysOfWeekData,
  reportRequestHandler(Dv360ReportType.DAYS_OF_WEEK),
);

export const getGoogleDaypartData = createAsyncThunk(
  Action.GetGoogleDaypartData,
  reportRequestHandler(Dv360ReportType.DAYPART),
);

export const getGoogleStateData = createAsyncThunk(
  Action.GetGoogleStateData,
  reportRequestHandler(Dv360ReportType.STATE),
);

export const getGoogleDmaData = createAsyncThunk(Action.GetGoogleDmaData, reportRequestHandler(Dv360ReportType.DMA));

export const getDmaBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  Action.GetDmaBoundaries,
  async (payload, { getState }) => {
    const {
      reportingDashboardGoogle: { dmaData },
    } = getState() as RootState;
    const { signal } = payload || {};
    const requestBody = getBoundariesRequestBody(MapScale.Dma, dmaData, BeeswaxReportProperties.DMA_CODE);
    if (!isPostableBoundariesBody(requestBody)) return EMPTY_FEATURE_COLLECTION;
    const responseResult = await axios.post(GEO_BOUNDARIES, requestBody, {
      signal,
    });

    return responseResult.data;
  },
);

export const getStateBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  Action.GetStateBoundaries,
  async (payload, { getState }) => {
    const {
      reportingDashboardGoogle: { stateData },
    } = getState() as RootState;
    const { signal } = payload || {};
    const requestBody = getBoundariesRequestBody(MapScale.State, stateData, BeeswaxReportProperties.GEO_STATE);
    if (!isPostableBoundariesBody(requestBody)) return EMPTY_FEATURE_COLLECTION;
    const responseResult = await axios.post(GEO_BOUNDARIES, requestBody, {
      signal,
    });

    return responseResult.data;
  },
);

/**date and campaign reports are handled separately on purpose */
export const reportMap: RequestMap<IState> = [
  ['totals', getGoogleReportTotals],
  ['appUrlReport', getGoogleAppUrlReport],
  ['channelData', getGoogleChannelData],
  ['deviceData', getGoogleDeviceData],
  ['advertiserData', getGoogleAdvertiserData],
  ['placementData', getGooglePlacementData],
  ['publisherData', getGooglePublisherData],
  ['creativeData', getGoogleCreativeData],
  ['daysOfWeekData', getGoogleDaysOfWeekData],
  ['daypartData', getGoogleDaypartData],
  ['stateData', getGoogleStateData],
  ['dmaData', getGoogleDmaData],
];

export const boundaryMap: RequestMap<IState> = [
  ['stateBoundariesData', getStateBoundaries],
  ['dmaBoundariesData', getDmaBoundaries],
];
